import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheck } from '@ng-icons/heroicons/outline';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiCheckbox,
  UiEmptyState,
  UiInput,
  UiLabel,
  UiPageHeader,
  UiPanelCard,
  UiRecordListDirective,
  UiRecordRow,
  UiRecordRowActions,
  UiRecordRowBody,
  UiRecordRowDescription,
  UiRecordRowMain,
  UiRecordRowMeta,
  UiRecordRowTitle,
  UiSkeletonGrid,
} from '../../../shared/ui';
import type { SlaPolicy } from '../../../core/api/generated/models/SlaPolicy';
import type { SlaTarget } from '../../../core/api/generated/models/SlaTarget';
import type { SlaTargetInput } from '../../../core/api/generated/models/SlaTargetInput';
import { ConversationsApiService } from '../data/conversations-api.service';
import { SLA_TARGET_SLOTS, type SlaTargetSlot, secondsToInputValue, slotInputValueToSeconds, slotKey } from '../sla/sla-target-slots';

/** Política com as metas já resolvidas, para a linha da lista não precisar cruzar duas coleções. */
interface PolicyRow {
  policy: SlaPolicy;
  targetsBySlot: Readonly<Record<string, number | null>>;
}

/**
 * Preferências de SLA: políticas e suas metas por (métrica, track).
 *
 * As metas moram em `sla_targets`, não mais em colunas da política — é a mesma tabela que o motor
 * de turno lê para decidir se um turno estourou, então o que se configura aqui é exatamente o que
 * o painel mede. Antes a tela escrevia duas colunas que só o selo do frontend usava, enquanto o
 * motor media contra uma tabela vazia.
 */
@Component({
  selector: 'app-conversation-sla-settings',
  imports: [
    NgIcon,
    ReactiveFormsModule,
    UiBadge,
    UiButton,
    UiCheckbox,
    UiEmptyState,
    UiInput,
    UiLabel,
    UiPageHeader,
    UiPanelCard,
    UiRecordListDirective,
    UiSkeletonGrid,
    UiRecordRow,
    UiRecordRowActions,
    UiRecordRowBody,
    UiRecordRowDescription,
    UiRecordRowMain,
    UiRecordRowMeta,
    UiRecordRowTitle,
  ],
  providers: [provideIcons({ heroCheck })],
  templateUrl: './sla-settings.page.html',
  styleUrl: './sla-settings.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlaSettingsPage {
  private readonly api = inject(ConversationsApiService);
  private readonly toast = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly rows = signal<readonly PolicyRow[]>([]);
  protected readonly saving = signal(false);
  protected readonly loading = signal(true);
  /**
   * Falha ao carregar. Distinta de "nenhuma política": sem este estado a tela afirmava que o negócio
   * não tem SLA configurado quando na verdade a leitura falhou.
   */
  protected readonly error = signal<string | null>(null);
  /** Política com o editor de metas aberto; null = nenhum. */
  protected readonly editing = signal<string | null>(null);

  protected readonly slots = SLA_TARGET_SLOTS;

  // ng-primitives exige ReactiveForms — o caminho [ngModel] quebra o overlay injector (NG0201).
  protected readonly nameControl = new FormControl<string>('', { nonNullable: true });
  protected readonly makeDefaultControl = new FormControl<boolean>(false, { nonNullable: true });
  /** Um controle por combinação; vazio = sem meta para aquela combinação. */
  protected readonly targetControls: Readonly<Record<string, FormControl<string>>> = Object.fromEntries(
    SLA_TARGET_SLOTS.map((slot) => [slot.key, new FormControl<string>('', { nonNullable: true })]),
  );

  constructor() {
    this.reload();
  }

  protected targetOf(row: PolicyRow, slot: SlaTargetSlot): string {
    const seconds = row.targetsBySlot[slot.key];
    return seconds === null || seconds === undefined ? 'sem meta' : slot.format(seconds);
  }

  protected openTargets(row: PolicyRow): void {
    this.editing.set(row.policy.id);
    for (const slot of SLA_TARGET_SLOTS) {
      const seconds = row.targetsBySlot[slot.key];
      this.targetControls[slot.key]!.setValue(secondsToInputValue(seconds ?? null, slot));
    }
  }

  protected closeTargets(): void {
    this.editing.set(null);
  }

  protected saveTargets(policyId: string): void {
    if (this.saving()) return;
    const targets: SlaTargetInput[] = [];
    for (const slot of SLA_TARGET_SLOTS) {
      const raw = this.targetControls[slot.key]!.value.trim();
      if (raw.length === 0) continue; // combinação sem meta: some do conjunto
      const seconds = slotInputValueToSeconds(raw, slot);
      if (seconds === null) {
        this.toast.error('Meta inválida', `Informe um número válido em "${slot.label}".`);
        return;
      }
      targets.push({ metric: slot.metric, track: slot.track, targetSeconds: seconds });
    }

    this.saving.set(true);
    this.api.replaceSlaTargets(policyId, { targets }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.editing.set(null);
        this.toast.success('Metas salvas', 'O painel passa a medir a adesão por estas metas.');
        this.reload();
      },
      error: (error: unknown) => {
        this.saving.set(false);
        this.toast.error('Não foi possível salvar as metas', this.messageFor(error));
      },
    });
  }

  protected create(): void {
    const name = this.nameControl.value.trim();
    if (name.length === 0 || this.saving()) return;
    this.saving.set(true);
    this.api
      .createSlaPolicy({ name, isDefault: this.makeDefaultControl.value, isActive: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.nameControl.setValue('');
          this.makeDefaultControl.setValue(false);
          this.reload();
        },
        error: (error: unknown) => {
          this.saving.set(false);
          this.toast.error('Não foi possível criar a política', this.messageFor(error));
        },
      });
  }

  protected toggleActive(policy: SlaPolicy): void {
    this.api.updateSlaPolicy(policy.id, { isActive: !policy.isActive }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.reload(),
      error: (error: unknown) => this.toast.error('Não foi possível atualizar a política', this.messageFor(error)),
    });
  }

  protected makeDefaultPolicy(policy: SlaPolicy): void {
    this.api.updateSlaPolicy(policy.id, { isDefault: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.reload(),
      error: (error: unknown) => this.toast.error('Não foi possível definir o padrão', this.messageFor(error)),
    });
  }

  protected retry(): void {
    this.reload();
  }

  private reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listSlaPolicies().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        const policies = response.data;
        if (policies.length === 0) {
          this.rows.set([]);
          this.loading.set(false);
          return;
        }
        // Uma chamada de metas por política. A lista é curta (uma política por negócio no MVP);
        // se virar dezenas, vale um endpoint que devolva tudo junto.
        forkJoin(
          policies.map((policy) =>
            this.api.listSlaTargets(policy.id, { silent: true }).pipe(catchError(() => of({ data: [] as SlaTarget[] }))),
          ),
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results) => {
          this.rows.set(policies.map((policy, index) => ({
            policy,
            targetsBySlot: indexBySlot(results[index]?.data ?? []),
          })));
          this.loading.set(false);
        });
      },
      error: (error: unknown) => {
        this.loading.set(false);
        // Estado de erro explícito, não só toast: o toast desaparece em segundos e a tela caía no
        // empty state, AFIRMANDO "nenhuma política criada". O gestor criava uma duplicada, ou
        // reconfigurava metas achando que estavam vazias e sobrescrevia as reais.
        this.error.set(this.messageFor(error));
        this.toast.error('Não foi possível carregar as políticas', this.messageFor(error));
      },
    });
  }

  private messageFor(error: unknown): string {
    const message = (error as { error?: { message?: string } } | undefined)?.error?.message;
    return message ?? 'Tente novamente em instantes.';
  }
}

function indexBySlot(targets: readonly SlaTarget[]): Record<string, number | null> {
  const bySlot: Record<string, number | null> = {};
  for (const slot of SLA_TARGET_SLOTS) bySlot[slot.key] = null;
  for (const target of targets) bySlot[slotKey(target.metric, target.track)] = target.targetSeconds;
  return bySlot;
}
